import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PageTitle from '../../components/PageTitle';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import EditClubIconModal from './EditClubIconModal';
import { useEffect, useState } from 'react';
import { useContext } from 'react';
import { UserContext } from '../../App';
import { updateOrganizer } from '../../api/Admin';
import { getUser } from '../../utils/Authentication';
import CustomToast from '../../components/CustomToast';
import Spinner from 'react-bootstrap/esm/Spinner';
import { ASSETS_ROOT } from '../../api/config';

function EditProfile() {
  const [modalShow, setModalShow] = useState(false);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [showEditOrganizerSuccess, setShowEditOrganizerSuccess] =
    useState(false);
  const [updating, setUpdating] = useState(false);
  const [imageUpdated, setImageUpdated] = useState(false);

  const userContext = useContext(UserContext);

  useEffect(() => {
    setForm({
      name: userContext.user?.clubName,
      organizationNumber: userContext.user?.organizationNumber,
      district: userContext.user?.district,
      swishNumber: userContext.user?.swishNumber,
    });
  }, [userContext]);

  useEffect(() => {
    async function fetchUser() {
      if (imageUpdated) {
        const user = await getUser();
        if (user) {
          userContext.setUser(user);
          setImageUpdated(false);
        }
      }
    }
    fetchUser();
  }, [imageUpdated, userContext]);

  const clubIcon = ASSETS_ROOT + userContext.user?.logoUrl;

  const districts = [
    'Gotlands BF',
    'Göteborgs BF',
    'Mellan Norrlands BF',
    'Nedre Norra BF',
    'Smålands BF',
    'Stockholms BF',
    'Söder-/ Västmanlands BF',
    'Södra BF',
    'Upplands BF',
    'Västra BF',
    'Östergötland BF',
    'Övre Norra BF',
  ];

  const setField = (field, value) => {
    setForm({
      ...form,
      [field]: value,
    });

    if (!!errors[field])
      setErrors({
        ...errors,
        [field]: null,
      });
  };

  const findFormErrors = () => {
    const { name, organizationNumber, district, swishNumber } = form;
    const newErrors = {};
    // name errors
    if (!name || name === '') {
      newErrors.name = 'Du måste ange ett namn.';
    }
    // organizationNumber errors
    if (!organizationNumber || organizationNumber === '') {
      newErrors.organizationNumber = 'Du måste ange ett organisationsnummmer.';
    } else if (/^[0-9]+$/.test(organizationNumber) === false) {
      newErrors.organizationNumber = 'Endast siffror tillåtet.';
    } else if (organizationNumber.length < 10) {
      newErrors.organizationNumber = 'För kort organisationsnummer.';
    } else if (organizationNumber.length > 10) {
      newErrors.organizationNumber = 'För långt organisationsnummer.';
    }
    // district errors
    if (!district || district === '') {
      newErrors.district = 'Du måste ange ett distrikt.';
    } else if (districts.includes(district) === false) {
      newErrors.district = 'Du måste ange ett distrikt.';
    }
    // swishNumber errors
    if (!swishNumber || swishNumber === '') {
      newErrors.swishNumber = 'Du måste ange ett Swish-nummer.';
    } else if (/^[0-9]+$/.test(swishNumber) === false) {
      newErrors.swishNumber = 'Endast siffror tillåtet.';
    } else if (swishNumber.toString().substring(0, 3) !== '123') {
      newErrors.swishNumber = 'Swish-numret måste vara ett företagsnummer.';
    } else if (swishNumber.length < 10) {
      newErrors.swishNumber = 'För kort Swish-nummer.';
    } else if (swishNumber.length > 10) {
      newErrors.swishNumber = 'För långt Swish-nummer.';
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    setUpdating(true);
    const newErrors = findFormErrors();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setUpdating(false);
    } else {
      const result = await updateOrganizer(form);
      if (result) {
        switch (result.statusCode) {
          case 200:
            const user = await getUser();
            userContext.setUser(user);
            setShowEditOrganizerSuccess(true);
        }
        setUpdating(false);
      }
    }
  };

  return (
    <div>
      <Row>
        <PageTitle
          title='Redigera klubbuppgifter'
          includeLinkBack={true}
          linkBackUrl='/organizer/competitions'
        />
      </Row>
      <Row>
        <Col>
          <p className='mt-3 text-secondary'>
            Uppgifterna visas i inbjudan till tävlingar.
          </p>
        </Col>
      </Row>
      <Row className='mt-4'>
        <Col md={4}>
          <Form>
            <h4>Klubblogga</h4>
            <div className='d-flex flex-column gap-2 mt-4'>
              <div className='d-flex justify-content-center'>
                {userContext.user?.logoUrl ? (
                  <img
                    src={clubIcon}
                    style={{ width: '250px' }}
                    alt='Klubbikon'
                  />
                ) : (
                  <span
                    className='material-symbols-outlined d-flex justify-content-center align-items-center'
                    style={{
                      fontSize: '250px',
                      backgroundColor: 'grey',
                      width: '250px',
                      height: '250px',
                    }}
                  >
                    flag
                  </span>
                )}
              </div>
              <Button
                className='mt-1'
                variant='primary'
                onClick={() => setModalShow(true)}
              >
                Byt klubblogga
              </Button>
              <div className='d-flex flex-column gap-2 mt-4'>
                <h4>Klubbuppgifter</h4>
                <Form.Group className='mt-2'>
                  <Form.Label>Klubbnamn</Form.Label>
                  <Form.Control
                    type='text'
                    defaultValue={form.name}
                    placeholder='Ange klubbnamn'
                    onChange={(e) => setField('name', e.target.value)}
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className='mt-2'>
                  <Form.Label>Organisationsnummer</Form.Label>
                  <Form.Control
                    type='text'
                    defaultValue={form.organizationNumber}
                    placeholder='Ange organisationsnummer'
                    onChange={(e) =>
                      setField('organizationNumber', e.target.value)
                    }
                    isInvalid={!!errors.organizationNumber}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.organizationNumber}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId='district' className='mt-2'>
                  <Form.Label>Distrikt</Form.Label>
                  <Form.Select
                    value={form.district}
                    onChange={(e) => setField('district', e.target.value)}
                    isInvalid={!!errors.district}
                  >
                    <option value='none' hidden>
                      Välj ett distrikt
                    </option>
                    <option value='Gotlands BF'>Gotlands BF</option>
                    <option value='Göteborgs BF'>Göteborgs BF</option>
                    <option value='Mellan Norrlands BF'>
                      Mellan Norrlands BF
                    </option>
                    <option value='Nedre Norra BF'>Nedre Norra BF</option>
                    <option value='Smålands BF'>Smålands BF</option>
                    <option value='Stockholms BF'>Stockholms BF</option>
                    <option value='Söder-/ Västmanlands BF'>
                      Söder-/ Västmanlands BF
                    </option>
                    <option value='Södra BF'>Södra BF</option>
                    <option value='Upplands BF'>Upplands BF</option>
                    <option value='Västra BF'>Västra BF</option>
                    <option value='Östergötland BF'>Östergötland BF</option>
                    <option value='Övre Norra BF'>Övre Norra BF</option>
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {errors.district}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className='mt-2'>
                  <Form.Label>Swish-nummer</Form.Label>
                  <Form.Control
                    type='text'
                    defaultValue={form.swishNumber}
                    disabled
                    placeholder='Ange Swish-nummer'
                    onChange={(e) => setField('swishNumber', e.target.value)}
                    isInvalid={!!errors.swishNumber}
                  />

                  <p className='text-secondary my-2'>Vänligen kontakta SBF om Swishnumret behöver 
                  ändras.</p>

                  <Form.Control.Feedback type='invalid'>
                    {errors.swishNumber}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className='d-flex justify-content-end'>
                  <Button onClick={handleSubmit} className='mt-2'>
                    {updating ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className='me-2'
                        />
                        <span className='visually-hidden'>Loading...</span>
                        Spara
                      </>
                    ) : (
                      'Spara'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </Col>
      </Row>
      <EditClubIconModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        setImageUpdated={setImageUpdated}
      />
      <CustomToast
        type='success'
        title='Arrangör uppdaterad'
        body='Arrangörens uppgifter har uppdaterats'
        show={showEditOrganizerSuccess}
        onClose={() => setShowEditOrganizerSuccess(false)}
      />
    </div>
  );
}

export default EditProfile;
